import React, { useState } from 'react';
import { Box, Typography, TextField, Divider, Tooltip } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import CollapsibleSection from '../../../shared/components/CollapsibleSection';
import AWSConsoleButton from '../../../shared/components/AWSConsoleButton';
import AWSCliCredentials from '../../../shared/components/AWSCliCredentials';
import { Customer } from '../../../types/backend';
import { OPERATIONS_ACCOUNT_REGION } from '../../../shared/constants/aws';

interface OperationsAccountViewProps {
  customer: Customer;
  allowWriteConsole?: boolean;
}

const OperationsAccountView: React.FC<OperationsAccountViewProps> = ({ customer, allowWriteConsole = false }) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const hasOpsConfig = !!(
    customer.operations_account_id &&
    customer.operations_account_region &&
    customer.operations_account_role
  );

  return (
    <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
      <Box sx={{ maxWidth: 800 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">
            Operations Account
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip
              title={!hasOpsConfig ? 'Console access requires account ID, region, and role to be configured' : ''}
              disableHoverListener={hasOpsConfig}
            >
              <span>
                <AWSConsoleButton
                  customerId={customer.id}
                  accountId={customer.operations_account_id ?? ''}
                  region={customer.operations_account_region ?? OPERATIONS_ACCOUNT_REGION}
                  allowWrite={allowWriteConsole}
                  disabled={!hasOpsConfig}
                />
              </span>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            <AWSCliCredentials
              customerId={customer.id}
              accountId={customer.operations_account_id ?? ''}
              allowWrite={allowWriteConsole}
              disabled={!hasOpsConfig}
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'grid', gap: 3 }}>
          <TextField
            fullWidth
            label="AWS Account ID"
            value={customer.operations_account_id}
            disabled
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Region"
            value={customer.operations_account_region || 'Not configured'}
            disabled
            InputLabelProps={{ shrink: true }}
          />

        </Box>

        <Box sx={{ mt: 3 }}>
          <CollapsibleSection
            title="Account Details"
            expanded={detailsExpanded}
            onChange={setDetailsExpanded}
          >
            <Box sx={{ display: 'grid', gap: 3 }}>
              <TextField
                fullWidth
                label="Read-Only Role"
                value={customer.operations_account_role || 'Not configured'}
                disabled
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Write Role"
                value={customer.operations_account_write_role || 'Not configured'}
                disabled
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Automation Role"
                value={customer.operations_account_automation_role || 'Not configured'}
                disabled
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Dataset curator role"
                value={customer.operations_account_dataset_role || 'Not configured'}
                disabled
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="External ID"
                value={customer.operations_account_external_id || 'Not configured'}
                disabled
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Event Bus Name"
                value={customer.operations_event_bus_name || 'Not configured'}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </CollapsibleSection>
        </Box>

        <InlineAlert severity="info" sx={{ mt: 3 }}>
          Operations account settings are managed in the Customer Management modal.
          This view is read-only.
        </InlineAlert>
      </Box>
    </Box>
  );
};

export default OperationsAccountView;
